/**
 * AIStylistService — Styliste IA professionnel
 *
 * Architecture en 4 couches :
 *   1. Computer Vision : analyse les photos des articles (Claude/GPT Vision)
 *   2. Scoring : évalue chaque combinaison /100 avec règles mode
 *   3. Détection manques : identifie ce qui manque pour un look complet
 *   4. Suggestions catalogue : recommande des articles CJ pertinents
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { DeviceEventEmitter } from 'react-native';
import { AWS_CONFIG } from '../config/aws_config';
import { normalizedNameKey } from '../utils/productNameUtils';

const API = AWS_CONFIG.apiUrl;

// ═══════════════════════════════════════════════════════════
// ÉVÉNEMENT : "ai_limit_reached"
// ═══════════════════════════════════════════════════════════
// Émis depuis AIStylistService (guard central) quand un appel Claude
// est bloqué car l'user a dépassé son quota journalier. Les écrans
// l'écoutent pour afficher le popup Premium une seule fois.
//
// Payload : { tier, current, limit, feature }
export const AI_LIMIT_EVENT = 'ai_limit_reached';

// Cache en mémoire : tant que ce flag est true, on bloque TOUS les
// appels IA sans même interroger PremiumService (évite spam async).
// Remis à false au prochain getCurrentDay() ou via reset externe.
let _aiQuotaExhausted = false;
let _aiQuotaExhaustedDay = null; // 'YYYY-MM-DD' du jour où on a flaggé
let _aiQuotaExhaustedAt = 0;     // timestamp ms du flag pour TTL
let _lastKnownLimit = null;       // Dernière limite connue (pour re-émettre l'event)
let _lastKnownTier = null;        // Dernier tier connu

// ═══ TTL du flag : 2 minutes ═══
// Avant : flag éternel jusqu'à minuit ou tier change. Problème : si admin reset
// le compteur (reset-ai-usage.sh), ou si le compteur backend est désynchro
// du flag local, le flag restait collé et popup s'affichait à tort même avec
// du solde disponible. 2 min = compromis entre "éviter spam backend" et
// "réactivité après reset / sync DB".
const AI_QUOTA_FLAG_TTL_MS = 2 * 60 * 1000;

function getCurrentDay() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

/**
 * Guard central avant tout appel IA coûteux (Claude Vision).
 * Retourne { allowed: boolean, tier, current, limit }
 *
 * - Consulte PremiumService.canUseAIOrIncrement() qui vérifie + incrémente
 * - Si refusé : flag local + émet AI_LIMIT_EVENT → popup s'affiche
 * - Les appels suivants sont court-circuités tant que le flag est actif
 */
async function guardAIQuota(user, feature = 'ai') {
  if (!user) return { allowed: false, tier: 'free', current: 0, limit: 0 };

  // Reset flag si on a changé de jour (minuit passé)
  const today = getCurrentDay();
  if (_aiQuotaExhaustedDay !== today) {
    _aiQuotaExhausted = false;
    _aiQuotaExhaustedDay = today;
    _aiQuotaExhaustedAt = 0;
    _lastKnownLimit = null;
    _lastKnownTier = null;
  }

  // ═══ FIX 2026-04 : TTL sur le flag mémoire ═══
  // Si le flag a été posé il y a > 2 min, on le re-vérifiez côté backend.
  // Cas couverts : admin reset compteur, désynchro mémoire ↔ DB, race condition
  // entre 2 incréments. Le user voit "Limite atteinte" pendant max 2 min, puis
  // l'app re-vérifiez et débloque automatiquement si la DB dit OK.
  if (_aiQuotaExhausted && _aiQuotaExhaustedAt > 0) {
    const ageMs = Date.now() - _aiQuotaExhaustedAt;
    if (ageMs > AI_QUOTA_FLAG_TTL_MS) {
      console.log(`[AIGuard] flag expiré (${Math.round(ageMs/1000)}s) → re-vérification DB`);
      _aiQuotaExhausted = false;
      _aiQuotaExhaustedAt = 0;
      _lastKnownLimit = null;
      _lastKnownTier = null;
    }
  }

  // ═══ FIX 2026-04 : reset si le tier a changé ═══
  // Si l'user était Premium (limite=30) puis son abonnement a expiré
  // (tier devient 'free', limite=3), le flag _aiQuotaExhausted peut rester
  // à true même si en réalité le compteur est à 0/3. Pareil dans l'autre sens
  // (free → premium après upgrade). On vérifiez le tier actuel avant de
  // court-circuiter sur le flag mémoire.
  if (_aiQuotaExhausted && _lastKnownTier) {
    try {
      const { getTier } = require('./PremiumService');
      const currentTier = await getTier(user);
      if (currentTier !== _lastKnownTier) {
        console.log(`[AIGuard] tier changed (${_lastKnownTier} → ${currentTier}) → reset flag`);
        _aiQuotaExhausted = false;
        _aiQuotaExhaustedAt = 0;
        _lastKnownLimit = null;
        _lastKnownTier = null;
      }
    } catch {}
  }

  // Shortcut : on sait déjà que le quota est épuisé aujourd'hui
  if (_aiQuotaExhausted) {
    console.log('[AIGuard] 🚫 quota déjà épuisé, appel bloqué sans aller en DB');
    // ═══ FIX : toujours émettre l'event pour que popup s'affiche ═══
    // Sinon, au 2e clic Analyser, l'event ne se déclenche plus et popup absent
    try {
      DeviceEventEmitter.emit(AI_LIMIT_EVENT, {
        tier:    _lastKnownTier || 'free',
        current: _lastKnownLimit || 0,
        limit:   _lastKnownLimit || 0,
        feature,
        cached:  true,
      });
    } catch {}
    return { allowed: false, tier: _lastKnownTier || 'free', current: _lastKnownLimit || 0, limit: _lastKnownLimit || 0, cached: true };
  }

  try {
    // v1.12.307 — FIX double-comptage : utiliser checkLimit (read-only) au lieu de
    // canUseAIOrIncrement. Le backend /analyze-item incrémente DÉJÀ ai_usage à
    // chaque appel — guardAIQuota ne doit JAMAIS incrémenter en plus côté front.
    const { checkLimit } = require('./PremiumService');
    const result = await checkLimit('ai', 0, user);

    if (!result.allowed) {
      _aiQuotaExhausted = true;
      _aiQuotaExhaustedDay = today;
      _aiQuotaExhaustedAt = Date.now();
      _lastKnownLimit = result.limit;
      _lastKnownTier = result.tier;
      console.log(`[AIGuard] 🚫 blocked: ${result.current}/${result.limit} (tier=${result.tier})`);

      // Émettre l'événement pour que les écrans affichent le popup Premium
      try {
        DeviceEventEmitter.emit(AI_LIMIT_EVENT, {
          tier:    result.tier,
          current: result.current,
          limit:   result.limit,
          feature,
        });
      } catch {}
    } else {
      // Si DB dit "allowed" mais flag mémoire était à true (cas désynchro après
      // reset admin par ex), on remet à false explicitement.
      if (_aiQuotaExhausted) {
        console.log('[AIGuard] ♻️ DB dit allowed mais flag était true → reset');
        _aiQuotaExhausted = false;
        _aiQuotaExhaustedAt = 0;
      }
    }
    return result;
  } catch (e) {
    console.warn('[AIGuard] error:', e.message);
    // En cas d'erreur, on laisse passer (fail-open pour ne pas casser l'UX)
    return { allowed: true, tier: 'free', current: 0, limit: 0 };
  }
}

/**
 * Reset manuel du flag (ex: logout, reset tier, passage Premium).
 * Permet aux appels IA de reprendre sans attendre minuit.
 */
export function resetAIQuotaFlag() {
  _aiQuotaExhausted = false;
  _aiQuotaExhaustedDay = null;
  _aiQuotaExhaustedAt = 0;
  console.log('[AIGuard] ♻️ flag reset');
}

// v1.13.55 — Reset AUSSI le flag quand le solde d'add-ons change (achat d'un
// pack de scans). Sans ça, un user free ayant épuisé ses scans gratuits puis
// achetant un pack restait bloqué côté app jusqu'au TTL 2 min (le tier ne
// changeant pas, le reset sur tier_changed ne se déclenchait pas).
try {
  const { DeviceEventEmitter } = require('react-native');
  DeviceEventEmitter.addListener('addon_balance_changed', () => {
    try { resetAIQuotaFlag(); } catch {}
  });
} catch {}

/**
 * Check non-bloquant : est-ce que les appels IA sont actuellement bloqués ?
 * Utile pour l'UI pour grisé un bouton sans faire d'appel.
 */
export function isAIQuotaExhausted() {
  const today = getCurrentDay();
  if (_aiQuotaExhaustedDay !== today) return false;
  // TTL : si flag posé il y a > 2 min, on le considère expiré
  if (_aiQuotaExhaustedAt > 0 && Date.now() - _aiQuotaExhaustedAt > AI_QUOTA_FLAG_TTL_MS) {
    return false;
  }
  return _aiQuotaExhausted;
}

// ═══════════════════════════════════════════════════════════
// PALETTES DE COULEURS — Théorie des couleurs mode
// ═══════════════════════════════════════════════════════════

// Neutres : vont avec tout
export const NEUTRALS = ['noir', 'blanc', 'gris', 'beige', 'crème', 'ivoire', 'taupe', 'écru'];

// Groupes harmoniques (analogues — se marient naturellement)
export const ANALOGOUS = {
  froids:    ['bleu', 'marine', 'violet', 'turquoise', 'vert'],
  chauds:    ['rouge', 'orange', 'jaune', 'corail', 'rouille', 'bordeaux'],
  terreux:   ['beige', 'camel', 'marron', 'cognac', 'rouille', 'kaki'],
  pastels:   ['rose', 'lavande', 'bleu ciel', 'vert menthe', 'pêche', 'crème'],
};

// Complémentaires (audacieux mais sophistiqué)
export const COMPLEMENTARY = {
  bleu:    ['orange', 'rouille', 'camel'],
  rouge:   ['vert', 'kaki', 'sauge'],
  jaune:   ['violet', 'prune', 'marine'],
  rose:    ['vert', 'kaki'],
};

// ═══════════════════════════════════════════════════════════
// SCORING FONCTIONS
// ═══════════════════════════════════════════════════════════

/**
 * Score de compatibilité couleur entre 2 articles (0-100)
 */
export function scoreColorCompat(colorA, colorB) {
  if (!colorA || !colorB) return 50; // inconnu → neutre
  const a = colorA.toLowerCase();
  const b = colorB.toLowerCase();
  if (a === b) return 60; // même couleur = OK mais monotone
  
  const aNeutral = NEUTRALS.some(n => a.includes(n));
  const bNeutral = NEUTRALS.some(n => b.includes(n));
  if (aNeutral && bNeutral) return 95; // 2 neutres = parfait (très chic)
  if (aNeutral || bNeutral) return 90; // 1 neutre + 1 couleur = excellent
  
  // Analogues
  for (const group of Object.values(ANALOGOUS)) {
    if (group.some(c => a.includes(c)) && group.some(c => b.includes(c))) {
      return 85;
    }
  }
  
  // Complémentaires (risqué mais peut être stylé)
  for (const [base, comps] of Object.entries(COMPLEMENTARY)) {
    if (a.includes(base) && comps.some(c => b.includes(c))) return 75;
    if (b.includes(base) && comps.some(c => a.includes(c))) return 75;
  }
  
  return 45; // sinon, couleurs qui jurent potentiellement
}

/**
 * Score formalité : distance entre 2 articles sur échelle 1-5
 */
export function scoreFormality(itemA, itemB) {
  const fA = itemA.ai_metadata?.formality || inferFormality(itemA);
  const fB = itemB.ai_metadata?.formality || inferFormality(itemB);
  const diff = Math.abs(fA - fB);
  if (diff === 0) return 100;
  if (diff === 1) return 90;
  if (diff === 2) return 70;
  if (diff === 3) return 40;
  return 20;
}

/**
 * Inférer la formalité sans metadata IA (fallback)
 */
export function inferFormality(item) {
  const n = (item.name || '').toLowerCase();
  const c = item.category || '';
  if (/soir|gala|cocktail|tuxedo|smoking|robe de/.test(n)) return 5;
  if (/blazer|costume|chemise|tailleur|talon|escarpin/.test(n)) return 4;
  if (/pull|chino|mocassin|polo|chemisier/.test(n)) return 3;
  if (/jean|t-shirt|sweat|hoodie|sneaker|basket/.test(n)) return 2;
  if (/short|jogging|survet|tongs|crocs/.test(n)) return 1;
  // Par catégorie
  if (c === 'Robes') return 4;
  if (c === 'Vestes') return 3;
  if (c === 'Manteaux') return 4; // un peu plus formel que vestes
  return 3;
}

/**
 * Classe un sac en sous-type pour un matching plus fin.
 * Retourne : 'clutch' | 'handbag' | 'crossbody' | 'tote' | 'backpack' | 'sport' | 'bag'
 */
export function classifyBag(item) {
  const n = ((item.name || '') + ' ' + (item.description || '')).toLowerCase();
  if (/pochette|clutch|minaudière/.test(n)) return 'clutch';
  if (/sac\s*de\s*sport|duffle|gym|fitness/.test(n)) return 'sport';
  if (/sac\s*à\s*dos|sac\s*a\s*dos|backpack|rucksack/.test(n)) return 'backpack';
  if (/bandoulière|bandouliere|crossbody|besace/.test(n)) return 'crossbody';
  if (/cabas|tote|shopping\s*bag|shopper/.test(n)) return 'tote';
  if (/sac\s*à\s*main|sac\s*a\s*main|handbag|sac\s*trendy|sac\s*ikonik/.test(n)) return 'handbag';
  if (/\bsac\b|\bbag\b|\bpurse\b/.test(n)) return 'handbag'; // générique → handbag par défaut femme
  return 'bag';
}

/**
 * Score de "féminité" d'un sac — plus c'est élevé, plus c'est un sac "femme classique".
 * handbag/clutch/tote → score haut · backpack/sport → score bas
 * Sert à prioriser dans le tri les sacs adaptés à une tenue femme.
 */
export function scoreBagGender(bag, gender = 'F') {
  const subtype = classifyBag(bag);
  if (gender === 'F') {
    const scores = {
      handbag:   100,
      clutch:    95,
      tote:      90,
      crossbody: 85,
      bag:       70,  // générique
      backpack:  30,  // sacs à dos moins prisés pour femme sauf sport
      sport:     15,
    };
    return scores[subtype] || 50;
  }
  // Homme
  const scoresM = {
    crossbody: 90,
    backpack:  85,
    tote:      75,
    handbag:   60,
    bag:       70,
    clutch:    30,
    sport:     80,
  };
  return scoresM[subtype] || 50;
}

/**
 * Vérifiez si un sac convient au contexte du look (occasion + formality).
 * Bloque les incohérences type "sac à dos avec robe de soirée".
 *
 * @param {object} bag       — Article sac du catalogue
 * @param {string} occasion  — 'Casual', 'Soirée', 'Bureau', 'Sport', etc.
 * @param {number} formality — 1-5, niveau moyen de formalité du look
 * @param {string} gender    — 'F' ou 'M'
 * @returns {boolean}
 */
export function isBagAppropriate(bag, occasion, formality, gender = 'F') {
  const subtype = classifyBag(bag);
  const occ = (occasion || '').toLowerCase();

  // Soirée / Cérémonie / formal → pas de sac à dos ni sport
  if (/soirée|soiree|cérémonie|ceremonie|gala/.test(occ) || formality >= 4) {
    if (subtype === 'backpack' || subtype === 'sport') return false;
    return true;
  }

  // Bureau → pas de sport, backpack OK si chic
  if (/bureau|work|business/.test(occ) || formality >= 3.5) {
    if (subtype === 'sport') return false;
    if (subtype === 'backpack') {
      const n = (bag.name || '').toLowerCase();
      if (/sport|nike|adidas|eastpak|carhartt|running|fitness|the north face/.test(n)) return false;
    }
    return true;
  }

  // Sport → uniquement sport / backpack
  if (/sport/.test(occ)) {
    return subtype === 'sport' || subtype === 'backpack';
  }

  // Casual / Week-end / tout le reste pour FEMME :
  // PRIORISER les sacs femme, ACCEPTER les sacs à dos seulement s'ils ne sont pas sportifs
  if (gender === 'F') {
    if (subtype === 'sport') return false;
    if (subtype === 'backpack') {
      const n = (bag.name || '').toLowerCase();
      // Refuser les sacs à dos visiblement sportifs/masculins
      if (/sport|nike|adidas|eastpak|carhartt|running|fitness|the north face|trekking|randonnée|outdoor|herschel/.test(n)) return false;
    }
    return true;
  }

  // Homme casual → tout sauf sport extrême
  if (subtype === 'sport') return false;

  return true;
}

/**
 * Score silhouette : éviter 2 pièces trop amples ensemble
 */
export function scoreSilhouette(itemA, itemB) {
  const fA = itemA.ai_metadata?.fit || 'regular';
  const fB = itemB.ai_metadata?.fit || 'regular';
  const isLoose = (f) => f === 'oversize' || f === 'ample';
  const isTight = (f) => f === 'slim' || f === 'cintré';
  
  if (isLoose(fA) && isLoose(fB)) return 40; // 2 amples = silhouette informe
  if (isTight(fA) && isTight(fB)) return 65; // 2 slim = moulant, pas idéal
  if (isLoose(fA) && isTight(fB)) return 95; // équilibre parfait
  if (isTight(fA) && isLoose(fB)) return 95;
  return 80;
}

/**
 * Score pattern : éviter plus de 2 motifs
 */
export function scorePatterns(items) {
  const patterns = items
    .map(i => i.ai_metadata?.pattern || 'uni')
    .filter(p => p !== 'uni');
  if (patterns.length === 0) return 100; // tous unis = classique
  if (patterns.length === 1) return 95;  // 1 motif = équilibre
  if (patterns.length === 2) return 70;  // 2 motifs = risqué
  return 30; // 3+ motifs = chaos
}

/**
 * Score saisonnalité selon température
 */
export function scoreSeason(item, tempC) {
  const meta = item.ai_metadata || {};
  const seasons = meta.seasonality || [];
  
  if (tempC < 10) { // hiver
    if (seasons.includes('hiver')) return 100;
    if (seasons.includes('automne')) return 80;
    if (seasons.includes('été')) return 20;
    return 60;
  }
  if (tempC < 18) { // automne/printemps
    if (seasons.includes('automne') || seasons.includes('printemps')) return 100;
    return 70;
  }
  if (tempC >= 23) { // été
    if (seasons.includes('été')) return 100;
    if (seasons.includes('printemps')) return 85;
    if (seasons.includes('hiver')) return 25;
    return 60;
  }
  return 80;
}

// ═══════════════════════════════════════════════════════════
// SCORE GLOBAL D'UNE TENUE
// ═══════════════════════════════════════════════════════════

export function scoreOutfit(items, tempC, occasion) {
  if (!items || items.length < 2) return 0;
  
  let colorScore = 0;
  let formalityScore = 0;
  let silhouetteScore = 0;
  const pairs = [];
  
  // Toutes les paires d'articles
  for (let i = 0; i < items.length; i++) {
    for (let j = i + 1; j < items.length; j++) {
      const a = items[i];
      const b = items[j];
      const cA = a.ai_metadata?.primaryColor || a.color || '';
      const cB = b.ai_metadata?.primaryColor || b.color || '';
      colorScore += scoreColorCompat(cA, cB);
      formalityScore += scoreFormality(a, b);
      silhouetteScore += scoreSilhouette(a, b);
      pairs.push([a, b]);
    }
  }
  colorScore      /= pairs.length;
  formalityScore  /= pairs.length;
  silhouetteScore /= pairs.length;
  
  const patternScore = scorePatterns(items);
  const seasonScore = items.reduce((s, i) => s + scoreSeason(i, tempC), 0) / items.length;
  
  // Pondération
  const total = 
      0.25 * colorScore
    + 0.20 * formalityScore
    + 0.15 * silhouetteScore
    + 0.15 * seasonScore
    + 0.15 * patternScore
    + 0.10 * 80; // versatilité base
  
  return Math.round(total);
}

// ═══════════════════════════════════════════════════════════
// DÉTECTION DES MANQUES
// ═══════════════════════════════════════════════════════════

/**
 * Retourne une liste de catégories manquantes pour compléter la tenue
 */
export function detectMissingPieces(outfit, tempC, occasion) {
  const missing = [];
  const categories = outfit.items.map(i => i.category);
  const hasRobe = categories.includes('Robes');
  const isCold = tempC < 15;
  const isVeryCold = tempC < 8;
  const isHot = tempC > 25;

  // ═══ PRIORITÉ 1 : TOPS / BAS / VESTES (vêtements principaux selon saison) ═══
  if (!hasRobe) {
    if (!categories.includes('Hauts')) {
      missing.push({ category: 'Hauts', reason: 'Haut manquant', priority: 'high', order: 1 });
    }
    if (!categories.includes('Bas')) {
      missing.push({ category: 'Bas', reason: 'Bas manquant', priority: 'high', order: 2 });
    }
  }

  // v1.12.205 + v1.12.210 : Veste et Manteau peuvent coexister (layering).
  // - tempC ≤ 8°C → Manteau requis + Veste optionnelle (layering blazer + manteau)
  // - 8°C < tempC ≤ 18°C → Veste requise, Manteau optionnel selon météo
  // - tempC > 18°C → ni l'un ni l'autre nécessaire
  const veryCold = tempC != null && tempC <= 8;
  const hasManteau = categories.includes('Manteaux');
  const hasVeste = categories.includes('Vestes');
  if (veryCold) {
    if (!hasManteau) {
      missing.push({
        category: 'Manteaux',
        reason: `Manteau recommandé (${tempC}°C, hiver)`,
        priority: 'high',
        order: 3,
      });
    }
    if (!hasVeste) {
      // Optionnel : permet le layering blazer + manteau
      missing.push({
        category: 'Vestes',
        reason: `Veste optionnelle pour layering`,
        priority: 'low',
        order: 4,
      });
    }
  } else if (isCold) {
    if (!hasVeste) {
      missing.push({
        category: 'Vestes',
        reason: `Veste recommandée (${tempC}°C, mi-saison)`,
        priority: 'high',
        order: 3,
      });
    }
    if (!hasManteau) {
      // Optionnel selon météo
      missing.push({
        category: 'Manteaux',
        reason: `Manteau optionnel selon humidité`,
        priority: 'low',
        order: 4,
      });
    }
  }

  // ═══ PRIORITÉ 2 : CHAUSSURES ═══
  if (!categories.includes('Chaussures')) {
    missing.push({
      category: 'Chaussures',
      reason: 'Aucune chaussure dans la tenue',
      priority: 'high',
      order: 4,
    });
  }

  // ═══ PRIORITÉ 3 : SAC (complète la silhouette) ═══
  if (!categories.includes('Sacs') && occasion !== 'Sport') {
    missing.push({
      category: 'Sacs',
      reason: 'Un sac complèterait la tenue',
      priority: 'medium',
      order: 5,
    });
  }

  // ═══ PRIORITÉ 4 : ACCESSOIRES (montre, collier en priorité) ═══
  // Reason précise pour que le scoring préfère montres/colliers
  if ((occasion === 'Soirée' || occasion === 'Chic' || occasion === 'Bureau') &&
      !categories.includes('Accessoires')) {
    missing.push({
      category: 'Accessoires',
      reason: 'Montre ou collier pour finaliser le look',
      priority: 'medium',
      order: 6,
      preferredKeywords: ['montre', 'collier', 'pendentif', 'sautoir', 'chaîne'],
    });
  }

  // ═══ PRIORITÉ 5 : CHAPEAU / CACHE-COL (saisonnier) ═══
  // Par froid → bonnet, écharpe, cache-col / par fort soleil → chapeau
  if (isVeryCold && !categories.includes('Accessoires')) {
    missing.push({
      category: 'Accessoires',
      reason: `Bonnet ou écharpe recommandé (${tempC}°C)`,
      priority: 'medium',
      order: 7,
      preferredKeywords: ['bonnet', 'echarpe', 'écharpe', 'cache', 'foulard', 'chapeau', 'beanie'],
    });
  } else if (isHot && !categories.includes('Accessoires')) {
    missing.push({
      category: 'Accessoires',
      reason: `Chapeau pour le soleil (${tempC}°C)`,
      priority: 'low',
      order: 7,
      preferredKeywords: ['chapeau', 'casquette', 'capeline', 'panama'],
    });
  }

  // ═══ PRIORITÉ 6 : BIJOUX (touche finale) ═══
  if ((occasion === 'Soirée' || occasion === 'Chic') &&
      !categories.includes('Bijoux')) {
    missing.push({
      category: 'Bijoux',
      reason: 'Bijoux pour la touche finale',
      priority: 'low',
      order: 8,
    });
  }

  // 💡 INSPIRATION : même si la tenue est "complète", toujours proposer des alternatives
  //    L'utilisateur a 3000 articles au catalogue, il veut VOIR des inspirations
  //    Inclure Hauts/Bas/Robes/Vestes aussi pour garantir la diversité
  //    — ordre respecté : Hauts/Bas/Vestes → Chaussures → Accessoires → Bijoux
  // v1.12.205 : Manteaux ajouté selon occasion (Chic et Bureau pour layering hiver)
  const INSPIRATION_CATS = {
    Casual:  ['Hauts', 'Bas', 'Robes', 'Vestes', 'Manteaux', 'Chaussures', 'Sacs', 'Accessoires', 'Bijoux'],
    Chic:    ['Hauts', 'Bas', 'Robes', 'Vestes', 'Manteaux', 'Chaussures', 'Sacs', 'Accessoires', 'Bijoux'],
    Soirée:  ['Robes', 'Hauts', 'Bas', 'Vestes', 'Manteaux', 'Chaussures', 'Sacs', 'Accessoires', 'Bijoux'],
    Bureau:  ['Hauts', 'Bas', 'Vestes', 'Manteaux', 'Chaussures', 'Sacs', 'Accessoires'],
    Sport:   ['Hauts', 'Bas', 'Chaussures', 'Accessoires'],
  };
  const inspirationCats = INSPIRATION_CATS[occasion] || INSPIRATION_CATS.Casual;
  let inspirationOrder = 20; // après les missings hard
  for (const cat of inspirationCats) {
    // Ne pas dupliquer si déjà dans missing
    if (missing.find(m => m.category === cat)) continue;
    missing.push({
      category: cat,
      reason: `Inspirations ${cat.toLowerCase()}`,
      priority: 'inspiration',
      order: inspirationOrder++,
    });
  }

  // ═══ TRI FINAL : par ordre de priorité défini ═══
  missing.sort((a, b) => (a.order || 99) - (b.order || 99));

  return missing;
}

// ═══════════════════════════════════════════════════════════
// SUGGESTIONS CATALOGUE
// ═══════════════════════════════════════════════════════════

/**
 * Trouve les meilleurs articles du catalogue pour compléter une tenue
 * @param {Object} outfit - la tenue
 * @param {Array} missing - catégories manquantes
 * @param {Array} catalogItems - catalogue complet
 * @param {Number} maxPerMissing - nb suggestions par catégorie manquante
 * @param {Set} excludeIds - ids déjà suggérés (à éviter)
 * @param {Number} poolSize - taille du pool depuis lequel piocher (défaut 30)
 */
export function findCatalogSuggestions(outfit, missing, catalogItems, maxPerMissing = 3, excludeIds = null, poolSize = 30) {
  const suggestions = {};
  const exclude = excludeIds || new Set();

  // ═══ OPTIMISATION : construire index une seule fois par catalogue ═══
  const { buildCatalogIndex, getCategoryCandidates, fastMatchScore, inferFormalityFast, inferColorFast, scoreWeatherMatch } = require('./CatalogIndexService');
  const idx = buildCatalogIndex(catalogItems);

  // Pré-calculer la moyenne formality du look ET les couleurs
  const outfitItems = outfit.items || [];
  if (outfitItems.length === 0) return suggestions;

  // Pré-calc une seule fois par outfit
  let outfitFormalitySum = 0;
  const outfitColors = [];
  for (const i of outfitItems) {
    const f = i.ai_metadata?.formality || inferFormalityFast(i);
    outfitFormalitySum += f;
    i._formality = f;
    i._color = i.ai_metadata?.primaryColor || i.color || inferColorFast(i);
    outfitColors.push(i._color);
  }
  const outfitFormality = outfitFormalitySum / outfitItems.length;
  const outfitOccasion = outfit.occasion || (missing.find(m => m.reason)?.reason) || '';
  const userGender = outfit.gender || 'F';
  // Température pour le scoring météo IA (optionnel)
  const tempC = typeof outfit.tempC === 'number' ? outfit.tempC : null;

  for (const miss of missing) {
    // Récupérer UNIQUEMENT les candidats de cette catégorie (pas de filter sur 5000 items)
    let candidates = getCategoryCandidates(idx, miss.category, 200);

    // Exclure les ids déjà suggérés
    if (exclude.size > 0) {
      candidates = candidates.filter(p => !exclude.has(p.id));
    }

    // Filtre spécifique sacs
    if (miss.category === 'Sacs') {
      candidates = candidates.filter(p => isBagAppropriate(p, outfitOccasion, outfitFormality, userGender));
    }

    // ═══ FILTRE GENDER IA (v2) : exclure les articles d'un autre genre ═══
    // Si l'IA a identifié le genre (F/M/unisex), on filtre
    candidates = candidates.filter(p => {
      const itemGender = p.ai_metadata?.gender;
      if (!itemGender || itemGender === 'unisex') return true;
      return itemGender === userGender;
    });

    // ═══ PRE-FILTRE MÉTÉO : éliminer les items clairement incompatibles ═══
    // Garde les items avec score météo > 25 (tolère légère inadéquation)
    if (tempC !== null && candidates.length > 50) {
      const filtered = candidates.filter(p => scoreWeatherMatch(p, tempC) > 25);
      // Seulement si le filtre ne vide pas trop le pool
      if (filtered.length >= 10) candidates = filtered;
    }

    if (candidates.length === 0) continue;

    // Scorer avec fastMatchScore (5-10× plus rapide que scoreColorCompat)
    // Pour un remplacement : on match avec les items du dressing SAUF celui remplacé
    const itemsToMatch = miss.replacesItemId
      ? outfitItems.filter(it => it.id !== miss.replacesItemId)
      : outfitItems;
    const scored = [];
    for (const p of candidates) {
      let colorScore = 0;
      if (itemsToMatch.length > 0) {
        // Moyenne des matchs avec chaque item du dressing restant
        for (const outfitItem of itemsToMatch) {
          colorScore += fastMatchScore(outfitItem, p);
        }
        colorScore /= itemsToMatch.length;
      } else {
        colorScore = 50; // neutre si pas d'autres items
      }

      // Formality match (basé sur les items restants si remplacement)
      const formalityRef = itemsToMatch.length > 0
        ? itemsToMatch.reduce((s, it) => s + (it._formality || inferFormalityFast(it)), 0) / itemsToMatch.length
        : outfitFormality;
      const candidateFormality = p._formality || inferFormalityFast(p);
      const formalityDiff = Math.abs(formalityRef - candidateFormality);
      const formalityScore = Math.max(0, 100 - formalityDiff * 20);

      // Bonus partenaires premium (v1.12.337 — ajout suit_negozi, ann_demeulemeester, coutr)
      // v1.12.856 — drestige ajouté (Awin 13% luxe homme + femme)
      const cjBonus = (p.partner === 'cashmere' || p.partner === 'iqueens'
                       || p.partner === 'suit_negozi' || p.partner === 'ann_demeulemeester'
                       || p.partner === 'coutr' || p.partner === 'drestige') ? 15 : 0;
      const promoBonus = (p.discount && p.discount > 20) ? 10 : 0;

      // Bonus genre pour Sacs
      let bagGenderBonus = 0;
      if (p.category === 'Sacs') {
        const bagScore = scoreBagGender(p, userGender);
        bagGenderBonus = (bagScore - 50) * 0.3;
      }

      // ═══ SCORING MÉTÉO : basé sur temperatureRange ou seasonality (IA) ═══
      const weatherScore = tempC !== null ? scoreWeatherMatch(p, tempC) : 50;

      // ═══ BONUS TENDANCE : +20 si le produit est trending ═══
      // trendScore (popularité du tag, 1-100) fournit un bonus proportionnel
      const trendingBonus = p.isTrending ? 20 + ((p.trendScore || 50) * 0.1) : 0;

      // ═══ BONUS MOTS-CLÉS PRÉFÉRÉS ═══
      // Ex : pour Accessoires en soirée → on préfère montres/colliers vs foulards
      // Ex : par grand froid → on préfère bonnets/écharpes
      let preferredKwBonus = 0;
      if (miss.preferredKeywords && miss.preferredKeywords.length > 0) {
        const productText = (
          (p.name || '') + ' ' +
          (p.category || '') + ' ' +
          (p.subcategory || '') + ' ' +
          (p.description || '')
        ).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        for (const kw of miss.preferredKeywords) {
          const kwNorm = kw.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          if (productText.includes(kwNorm)) {
            preferredKwBonus = 25; // un gros boost pour que ces items remontent
            break;
          }
        }
      }

      // Pondération finale :
      //   Couleur 0.4 + Formality 0.25 + Météo 0.2 + Bonus CJ/promo/sac/trending/keywords
      const score = colorScore * 0.4 + formalityScore * 0.25 + weatherScore * 0.2
                  + cjBonus + promoBonus + bagGenderBonus + trendingBonus + preferredKwBonus;
      scored.push({ ...p, _stylistScore: Math.round(score), _missReason: miss.reason, _weatherScore: weatherScore });
    }

    // Trier + top pool
    scored.sort((a, b) => b._stylistScore - a._stylistScore);
    const topPool = scored.slice(0, poolSize);

    // Sélection pondérée (weighted random sans remise)
    const picked = [];
    const pool = [...topPool];
    for (let i = 0; i < maxPerMissing && pool.length > 0; i++) {
      const totalWeight = pool.reduce((s, p) => s + Math.pow(Math.max(p._stylistScore, 1), 2), 0);
      let rnd = Math.random() * totalWeight;
      let picked_idx = 0;
      for (let j = 0; j < pool.length; j++) {
        rnd -= Math.pow(Math.max(pool[j]._stylistScore, 1), 2);
        if (rnd <= 0) { picked_idx = j; break; }
      }
      picked.push(pool[picked_idx]);
      pool.splice(picked_idx, 1);
    }

    suggestions[miss.category] = picked;
    picked.forEach(p => exclude.add(p.id));
  }

  return suggestions;
}

// ═══════════════════════════════════════════════════════════
// COMPUTER VISION (backend Lambda)
// ═══════════════════════════════════════════════════════════

// Verrous pour éviter les appels doublons
const _analyzeInFlight = new Map(); // itemId → Promise
let _batchRunning = false;           // un seul batch à la fois
let _lastBatchTime = 0;              // timestamp du dernier batch (debounce)
const BATCH_DEBOUNCE_MS = 10000;     // 10s min entre deux batchs

/**
 * Demande au backend d'analyser une image d'article avec Claude Vision
 * Met en cache le résultat pour éviter de réanalyser
 */
// Helper pour récupérer le token Cognito — délègue à authHelpers (pas d'AWS Amplify)
async function getAuthToken() {
  try {
    const { getAuthToken: helper } = await import('./authHelpers');
    return await helper();
  } catch (e) {
    console.warn('[AIStylist] getAuthToken:', e.message);
    return null;
  }
}

// Extrait le sub Cognito depuis un JWT (sans vérif signature — juste pour tracking local)
async function _getUserIdFromToken(token) {
  try {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64 standard
    const payloadB64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // Décodage (React Native supporte atob, sinon fallback Buffer)
    let payloadStr;
    try {
      payloadStr = atob(payloadB64 + '==='.slice(0, (4 - payloadB64.length % 4) % 4));
    } catch {
      payloadStr = Buffer.from(payloadB64, 'base64').toString('utf8');
    }
    const payload = JSON.parse(payloadStr);
    return payload?.sub || null;
  } catch {
    return null;
  }
}

export async function analyzeItemImage(item, authToken, user = null) {
  if (!item || !item.image) {
    console.log('[AIStylist] skip item (no image):', item?.id);
    return null;
  }
  // Déjà analysé (DB) ?
  if (item.ai_metadata?.analyzedAt) {
    return item.ai_metadata;
  }

  // v1.12.252 — FIX double-comptage : on vérifie d'abord le cache local AVANT
  // d'appeler guardAIQuota. Si l'item est déjà analysé en cache, on retourne
  // sans consommer le quota IA. Avant : guard incrémentait avant le check cache,
  // ce qui faisait monter le compteur free à 3/3 sur des analyses déjà faites.
  const cacheKey = `ai_analysis_${item.id}`;
  let cachedMeta = null;
  try {
    const cached = await AsyncStorage.getItem(cacheKey);
    if (cached) {
      cachedMeta = JSON.parse(cached);
      console.log('[AIStylist] cache hit (pre-guard):', item.id, '→ skip quota');
    }
  } catch {}
  if (cachedMeta) {
    // Cache hit : pas d'appel IA, pas d'incrémentation. Mais on persiste en DB
    // si pas encore fait.
    if (!item.ai_metadata?.analyzedAt) {
      try {
        let token = authToken;
        if (!token) token = await getAuthToken();
        if (token) {
          await fetch(`${API}/analyze-item-persist`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({ itemId: item.id, metadata: cachedMeta }),
          });
        }
      } catch {}
    }
    return cachedMeta;
  }

  // ═══ FIX 2026-04 : garde quota IA centrale ═══
  // Cache miss → analyse Claude réelle nécessaire → on consomme le quota.
  // Si épuisé → émet AI_LIMIT_EVENT (popup Premium) + retourne _quotaExceeded.
  if (user) {
    const guard = await guardAIQuota(user, 'ai');
    if (!guard.allowed) {
      console.log('[AIStylist] ⛔ analyzeItemImage bloqué par quota IA');
      // v1.13.56 — GA : erreur affichée au client lors de l'ajout au dressing
      try { require('./AnalyticsService').trackEvent('wardrobe_add_error', { reason: 'ai_quota_reached', source: 'analyze', tier: guard.tier, limit: guard.limit, current: guard.current, category: item?.category || null }); } catch {}
      return {
        _quotaExceeded: true,
        _tier:    guard.tier,
        _current: guard.current,
        _limit:   guard.limit,
      };
    }
  }

  // Verrou : si une analyse du même item est déjà en cours, attendre son résultat
  if (_analyzeInFlight.has(item.id)) {
    return _analyzeInFlight.get(item.id);
  }

  const promise = (async () => {
    // v1.12.252 — Cache déjà checké en haut de fonction (ligne ~875). On entre
    // ici uniquement quand cache miss → vrai appel Claude (consomme quota IA).
    try {
      let token = authToken;
      if (!token) token = await getAuthToken();
      if (!token) {
        console.warn('[AIStylist] ❌ no token available — skipping analysis');
        return null;
      }

      console.log('[AIStylist] 🎨 analyzing item:', item.name, '| image:', (item.image || '').slice(0, 60));

    // v1.13.62 — TIMEOUT 35s : sans ça, si le backend /analyze-item (Claude Vision,
    // cold Lambda) ne répond pas, l'ajout restait BLOQUÉ indéfiniment sur "analyse
    // en cours". Sur timeout → AbortError → catch → return null → ajout annulé
    // proprement (message d'erreur, pas de blocage).
    const _aiCtl = new AbortController();
    const _aiTo = setTimeout(() => { try { _aiCtl.abort(); } catch {} }, 35000);
    let res;
    try {
      res = await fetch(`${API}/analyze-item`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          itemId: item.id,
          imageUrl: item.image,
          name: item.name,
          category: item.category,
        }),
        signal: _aiCtl.signal,
      });
    } finally { clearTimeout(_aiTo); }
    
    if (!res.ok) {
      console.warn('[AIStylist] HTTP', res.status, 'for', item.name);
      try { require('./AnalyticsService').trackEvent('wardrobe_add_error', { reason: 'analysis_http_error', source: 'analyze', status: res.status, category: item?.category || null }); } catch {}
      return null;
    }
    const data = await res.json();

    // ═══ DIAGNOSTIC : log de la structure reçue ═══
    console.log('[AIStylist] 📦 réponse backend pour', item.name, ':', JSON.stringify({
      rejected:     data.rejected,
      isClothing:   data.isClothing,
      photoIssue:   data.photoIssue,
      serverReason: data._serverReason || null,
      hasMetadata:  !!data.metadata,
      metaIsClothing: data.metadata?.isClothing,
      metaPhotoIssue: data.metadata?.photoIssue,
      metaInferred:   data.metadata?.inferred,
    }));
    if (data._serverReason) {
      console.warn(`[AIStylist] ⚠️ server reason: ${data._serverReason}`);
    }

    // ═══ FIX 2026-04 : gérer le rejet pour photos non-vêtement ═══
    // Le backend renvoie { rejected: true, isClothing: false, photoIssue, userMessage }
    // quand Claude détecte que l'image n'est pas un vêtement ou est inexploitable
    // (flou, sombre, coupée, etc).
    //
    // Alternative : Claude peut aussi mettre isClothing=false DANS metadata
    // si l'ancien Lambda tourne encore (pas de logique rejected/userMessage
    // au niveau racine). On gère les 2 cas.
    const rejectedAtRoot  = data.rejected === true || data.isClothing === false;
    const rejectedInMeta  = data.metadata?.isClothing === false;

    // ═══ FIX 2026-04 : détecter le fallback texte côté Lambda ═══
    // Si le backend a retourné une metadata générique (pas de vraie analyse
    // Claude Vision), on traite comme rejet. Signatures du fallback :
    //  - metadata.inferred === true
    //  - metadata.notes === "analyse automatique basée sur le nom"
    //  - absence de styleScores (signe qu'on est en fallback)
    // Ce cas arrive quand :
    //  - la clé ANTHROPIC_API_KEY est absente du Lambda
    //  - l'image ne peut pas être fetchée par le Lambda (URL inaccessible)
    //  - le Lambda n'a pas été redéployé avec le dernier prompt
    // v1.12.325 — FIX faux-rejet : on ne se base QUE sur les signaux explicites
    //   du Lambda (inferred:true ou notes spécifique). L'ancienne heuristique
    //   "!styleScores && !isClothing" rejetait à tort les analyses Claude
    //   valides quand le prompt ne demande pas ces 2 champs en sortie.
    const isFallbackTextOnly = data.metadata?.inferred === true
      || data.metadata?.notes === 'analyse automatique basée sur le nom';

    if (rejectedAtRoot || rejectedInMeta) {
      const photoIssue = data.photoIssue
        || data.metadata?.photoIssue
        || 'not_clothing';
      const messages = {
        not_clothing:    "Nous n'avons pas reconnu de vêtement sur cette photo. 👗\nEssayez à nouveau avec un vêtement ou accessoire visible en entier.",
        blurry:          "La photo semble un peu floue. 📸\nEssayez de stabiliser votre téléphone et de refaire la photo.",
        too_dark:        "L'éclairage est insuffisant pour analyser le vêtement. 💡\nPlacez-vous dans un endroit bien éclairé et réessayez.",
        cut_off:         "Le vêtement n'apparaît pas en entier sur la photo. 📐\nReculez un peu pour capturer tout l'article.",
        multiple_items:  "Plusieurs vêtements sont visibles sur la photo. 👕\nPrenez une photo avec un seul article à la fois.",
      };
      const userMessage = data.userMessage
        || messages[photoIssue]
        || messages.not_clothing;

      console.warn(`[AIStylist] 🚫 photo rejetée : ${photoIssue} (source=${rejectedAtRoot ? 'root' : 'metadata'})`);
      try { require('./AnalyticsService').trackEvent('wardrobe_add_error', { reason: 'photo_rejected', source: 'analyze', photo_issue: photoIssue, category: item?.category || null }); } catch {}
      return {
        _rejected:    true,
        _photoIssue:  photoIssue,
        _userMessage: userMessage,
      };
    }

    if (isFallbackTextOnly) {
      console.warn(`[AIStylist] ⚠️ FALLBACK TEXTE détecté côté Lambda pour "${item.name}" → traité comme rejet`);
      try { require('./AnalyticsService').trackEvent('wardrobe_add_error', { reason: 'analysis_fallback', source: 'analyze', category: item?.category || null }); } catch {}
      return {
        _rejected:    true,
        _photoIssue:  'analysis_failed',
        _userMessage:
          "L'IA n'a pas pu analyser cette photo correctement.\n\n" +
          "L'article n'a pas été ajouté au dressing.\n\n" +
          "📸 Réessayez avec une photo claire d'un vêtement, chaussure, sac ou accessoire mode.\n\n" +
          "(Si le problème persiste, contactez-nous à support@mytenue.com.)",
      };
    }

    if (!data.metadata) {
      console.warn('[AIStylist] no metadata returned for', item.name);
      return null;
    }
    
    // ═══ LOG COMPLET DE L'ANALYSE IA ═══
    const m = data.metadata;
    console.log('[AIStylist] ✅ ANALYSE COMPLÈTE:', item.name);
    console.log('  🎨 Couleur principale  :', m.primaryColor || '—');
    if (Array.isArray(m.secondaryColors) && m.secondaryColors.length) {
      console.log('  🎨 Couleurs secondaires :', m.secondaryColors.join(', '));
    }
    console.log('  🔲 Motif                :', m.pattern || '—');
    console.log('  🧵 Matière              :', m.material || '—');
    console.log('  📐 Coupe (fit)          :', m.fit || '—');
    console.log('  📏 Longueur             :', m.length || '—');
    console.log('  🎩 Formalité (/10)     :', m.formality ?? '—');
    if (Array.isArray(m.seasonality) && m.seasonality.length) {
      console.log('  🌦️  Saisons              :', m.seasonality.join(', '));
    }
    if (Array.isArray(m.temperatureRange) && m.temperatureRange.length === 2) {
      console.log('  🌡️  Plage température   :', `${m.temperatureRange[0]}°C – ${m.temperatureRange[1]}°C`);
    }
    if (Array.isArray(m.styleTags) && m.styleTags.length) {
      console.log('  ✨ Tags style           :', m.styleTags.join(', '));
    }
    console.log('  🔄 Versatilité (/10)   :', m.versatility ?? '—');
    if (m.notes) console.log('  💬 Notes                :', m.notes);

    // ═══ NOTE : le compteur IA a déjà été incrémenté en amont par guardAIQuota() ═══
    // (via PremiumService.canUseAIOrIncrement qui vérifiez ET incrémente).
    // Donc PAS d'incrément ici, sinon chaque appel compte pour 2.

    // Cache (on cache la metadata pure, pas les champs de validation nom)
    // v1.12.328 — Safari localStorage limite ~5MB : throws silencieusement quand
    //             plein. On rend le set non-fatal pour ne pas casser l'analyse.
    try {
      await AsyncStorage.setItem(cacheKey, JSON.stringify(data.metadata));
    } catch (cacheErr) {
      console.warn('[AIStylist] cache setItem failed (continuing):', cacheErr?.message);
    }

    // ═══ FIX 2026-04 : attacher les infos de validation nom à la metadata ═══
    // L'appelant (WardrobeScreen) peut ainsi détecter un mismatch et proposer
    // le nom suggéré à l'user. Ces champs sont préfixés _ pour indiquer qu'ils
    // ne font pas partie du schéma DB mais sont des metadata runtime.
    const nameMatches = data.nameMatchesPhoto !== false
      && data.metadata?.nameMatchesPhoto !== false;
    if (!nameMatches && (data.suggestedName || data.metadata?.suggestedName)) {
      console.log(`[AIStylist] 📝 nom ne correspond pas — suggéré: "${data.suggestedName || data.metadata?.suggestedName}"`);
    }

    // v1.12.524 — Exposer aussi le mismatch catégorie (Hauts/Bas/Robes/etc.)
    const categoryMatches = data.categoryMatchesUser !== false;
    if (!categoryMatches) {
      console.log(`[AIStylist] 📦 catégorie ne correspond pas — user="${data.userCategory}" detected="${data.detectedCategory}"`);
    }

    return {
      ...data.metadata,
      _nameMismatch:      !nameMatches,
      _suggestedName:     data.suggestedName || data.metadata?.suggestedName || null,
      _detectedType:      data.detectedType || data.metadata?.detectedType || null,
      _categoryMismatch:  !categoryMatches,
      _detectedCategory:  data.detectedCategory || data.metadata?.category || null,
      _userCategory:      data.userCategory || null,
    };
  } catch (e) {
    console.warn('[AIStylist] analyze error:', e.message);
    try { require('./AnalyticsService').trackEvent('wardrobe_add_error', { reason: 'analysis_exception', source: 'analyze', detail: (e?.message || '').slice(0, 100), category: item?.category || null }); } catch {}
    return null;
  }
  })();

  _analyzeInFlight.set(item.id, promise);
  try {
    return await promise;
  } finally {
    _analyzeInFlight.delete(item.id);
  }
}

// ═══════════════════════════════════════════════════════════

/**
 * Analyser plusieurs articles en batch (non-bloquant, arrière-plan)
 * @param {Array} wardrobeItems
 * @param {String} authToken - token Cognito (recommandé) ou null (fallback Amplify)
 */
export async function analyzeWardrobeInBackground(wardrobeItems, authToken, user = null) {
  if (!Array.isArray(wardrobeItems) || wardrobeItems.length === 0) {
    return [];
  }
  // Verrou global : si un batch tourne déjà, sortir silencieusement
  if (_batchRunning) {
    return [];
  }

  // v1.12.252 — Garde préalable RETIRÉE : guardAIQuota appelait
  // canUseAIOrIncrement qui consomme du quota MEME si tous les items du
  // dressing sont déjà en cache (cache hit dans analyzeItemImage). Le guard
  // est maintenant délégué à analyzeItemImage qui ne consomme du quota
  // QUE si un vrai appel Claude est nécessaire (cache miss).
  // Debounce : ignorer si un batch vient de finir il y a moins de 10s
  const now = Date.now();
  if (now - _lastBatchTime < BATCH_DEBOUNCE_MS) {
    return [];
  }
  _batchRunning = true;
  try {
    // ═══ SCHEMA VERSION : v2 = enrichi (styleScores, gender, isTrendy, occasions) ═══
    // Articles analysés avec l'ancien schéma (v1 = juste styleTags) sont re-analysés
    const SCHEMA_VERSION = 2;
    const needsReanalysis = (item) => {
      if (!item || !item.image) return false;
      const meta = item.ai_metadata;
      if (!meta?.analyzedAt) return true; // jamais analysé
      // Schéma obsolète : manque styleScores ou gender → re-analyser
      if (!meta.styleScores || typeof meta.gender === 'undefined') return true;
      // Si schemaVersion explicite et < courant → re-analyser
      if (meta.schemaVersion && meta.schemaVersion < SCHEMA_VERSION) return true;
      return false;
    };
    const unanalyzed = wardrobeItems.filter(needsReanalysis);
    const alreadyAnalyzed = wardrobeItems.filter(i => i?.ai_metadata?.analyzedAt).length;
    const v1Legacy = wardrobeItems.filter(i => i?.ai_metadata?.analyzedAt && !i.ai_metadata.styleScores).length;
    console.log(`[AIStylist] wardrobe: ${wardrobeItems.length} items | analysés: ${alreadyAnalyzed} | v1→v2 legacy: ${v1Legacy} | à analyser: ${unanalyzed.length}`);
    if (unanalyzed.length === 0) return [];

    // Analyser tous les articles non analysés (pas de limite 3)
    // Les appels séquentiels évitent de surcharger Claude Vision API
    const results = [];
    for (const item of unanalyzed) {
      // Forcer la re-analyse : supprimer le cache local et l'ancien ai_metadata
      try {
        await AsyncStorage.removeItem(`ai_analysis_${item.id}`);
      } catch {}
      const itemForAnalysis = { ...item, ai_metadata: null }; // force analyse fraîche
      // v1.12.252 — Background batch : passe user=null pour ne PAS consommer le
      // quota IA personnel (le batch tourne silencieusement et le coût d'analyse
      // est porté par Mytenue, pas par l'user). L'user ne voit le compteur monter
      // que sur ses ajouts explicites (photo upload / catalog add).
      const metadata = await analyzeItemImage(itemForAnalysis, authToken, null);
      // ═══ FIX 2026-04 : si un item a déclenché le quota → stopper tout le batch ═══
      if (metadata && metadata._quotaExceeded) {
        console.log('[AIStylist-Batch] ⛔ quota épuisé pendant le batch, arrêt');
        break;
      }
      if (metadata) results.push({ itemId: item.id, metadata });
    }
    console.log(`[AIStylist] batch done: ${results.length}/${unanalyzed.length} succès (schéma v${SCHEMA_VERSION})`);
    // Émettre un event pour que Home/Profile rafraîchissent leur compteur
    if (results.length > 0) {
      try {
        const { DeviceEventEmitter } = require('react-native');
        DeviceEventEmitter.emit('wardrobe_updated', { aiAnalyzed: results.length });
      } catch {}
    }
    return results;
  } finally {
    _batchRunning = false;
    _lastBatchTime = Date.now();
  }
}

/**
 * Enrichir un catalogue de produits avec leurs metadata IA depuis la DB globale
 * → Un seul appel réseau pour N produits (batch get)
 * → Cache local AsyncStorage (évite de re-fetcher à chaque ouverture)
 */
export async function enrichCatalogWithAI(catalogItems, authToken) {
  if (!Array.isArray(catalogItems) || catalogItems.length === 0) return catalogItems;
  if (!authToken) {
    authToken = await getAuthToken();
    if (!authToken) return catalogItems;
  }

  // Extraire les ids
  const ids = catalogItems.map(p => p.id).filter(Boolean);
  if (ids.length === 0) return catalogItems;

  // Vérifier cache local
  const cacheKey = 'catalog_ai_metadata_v1';
  let cached = {};
  try {
    const raw = await AsyncStorage.getItem(cacheKey);
    if (raw) cached = JSON.parse(raw) || {};
  } catch {}

  // Quels ids ont besoin d'être fetch ?
  const missing = ids.filter(id => !cached[id]);

  // ═══ OPTI 2026-04 : skip l'appel réseau si couverture locale suffisante ═══
  // Avant : on appelait toujours /catalog-analysis même si 99% du catalogue
  // était en cache. Maintenant : si < 5% des produits manquent, on skip et
  // on utilise juste le cache. Le cas manquant sera couvert au batch suivant.
  const missingRatio = missing.length / ids.length;
  if (missingRatio < 0.05 && Object.keys(cached).length > 0) {
    console.log(`[AIStylist-Enrich] 💾 cache hit >95% (${ids.length - missing.length}/${ids.length}) — skip network`);
  } else if (missing.length > 0) {
    console.log(`[AIStylist-Enrich] 🌐 fetch missing: ${missing.length}/${ids.length}`);
    // Fetch en batch depuis le backend (max 500 par appel)
    for (let i = 0; i < missing.length; i += 500) {
      const chunk = missing.slice(i, i + 500);
      try {
        const res = await fetch(`${API}/catalog-analysis`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
          body: JSON.stringify({ productIds: chunk }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.byId) {
            Object.assign(cached, data.byId);
          }
        }
      } catch(e) { console.warn('[AIStylist] enrich fetch:', e.message); }
    }
    // Sauvegarder en cache local
    try { await AsyncStorage.setItem(cacheKey, JSON.stringify(cached)); } catch {}
  }

  // Enrichir chaque produit avec son metadata IA (si dispo)
  let enrichedCount = 0;
  const enriched = catalogItems.map(p => {
    if (p.id && cached[p.id]) {
      enrichedCount++;
      return { ...p, ai_metadata: cached[p.id] };
    }
    return p;
  });
  console.log(`[AIStylist] catalog enriched: ${enrichedCount}/${catalogItems.length}`);
  return enriched;
}

/**
 * Déclencher l'analyse IA d'un produit catalogue (en arrière-plan)
 * → Stockage partagé entre tous les users (rentabilisé)
 */
export async function analyzeCatalogItem(product, authToken) {
  if (!product || !product.id || !product.image) return null;
  if (!authToken) {
    authToken = await getAuthToken();
    if (!authToken) return null;
  }
  try {
    const res = await fetch(`${API}/analyze-catalog-item`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({
        productId: product.id,
        partner:   product.partner,
        imageUrl:  product.image,
        name:      product.name,
        category:  product.category,
      }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.metadata || null;
  } catch(e) {
    console.warn('[AIStylist] analyzeCatalogItem:', e.message);
    return null;
  }
}

/**
 * Analyser en arrière-plan N produits catalogue non analysés
 * Verrou global pour éviter plusieurs batchs simultanés
 *
 * OPTI 2026-04 :
 *  - Debounce 5 min (au lieu de 30s) → moins d'appels pour le user qui navigue
 *  - Early exit si aucun produit éligible (pas d'appel réseau pour rien)
 *  - AsyncStorage flag pour éviter les re-analyses même après redémarrage app :
 *    on mémorise la date du dernier batch complet → au prochain démarrage,
 *    si < 1h, on skip complètement.
 */
let _catalogBatchRunning = false;
let _lastCatalogBatchTime = 0;
const CATALOG_BATCH_DEBOUNCE_MS = 5 * 60 * 1000;      // 5 min entre batchs (au lieu de 30s)
const CATALOG_BATCH_COLD_SKIP_MS = 60 * 60 * 1000;    // 1h après un batch complet → skip même si redémarrage

export async function analyzeCatalogInBackground(catalogItems, authToken, maxPerBatch = 5, user = null) {
  // ═══ v1.10.21 : Analyse côté client DÉSACTIVÉE — cron serveur prend le relais ═══
  //
  //   Avant (v1.10.20) : on analysait les produits IQueens côté client car ils
  //   n'étaient pas en DB.
  //
  //   Maintenant : le cron Lambda `outfit-iqueens-catalog-cron` (2h UTC) scrape
  //   iQueens et insère dans `partner_products`. Le cron `outfit-catalog-analysis-cron`
  //   (4h UTC) analyse ensuite tous les produits avec Claude Vision.
  //
  //   Bénéfices :
  //     - Coût analyse : × 1 (au lieu de × N users)
  //     - Pas de drain quota IA des users
  //     - Pas de latence réseau (analyse en arrière-plan côté serveur)
  //
  //   On garde le code pour fallback en cas de panne du cron, mais on early-exit
  //   immédiatement (les produits iQueens en DB ont déjà ai_metadata).
  return [];
  // eslint-disable-next-line no-unreachable
  if (!Array.isArray(catalogItems) || catalogItems.length === 0) return [];

  // Garde quota IA si user fourni
  if (user) {
    const guard = await guardAIQuota(user, 'ai');
    if (!guard.allowed) {
      console.log('[AIStylist-Catalog] ⛔ batch bloqué par quota IA');
      return [];
    }
  }

  // Debounce 5 min entre batchs
  if (_catalogBatchRunning) return [];
  const now = Date.now();
  if (now - _lastCatalogBatchTime < CATALOG_BATCH_DEBOUNCE_MS) return [];

  // Filtrer : uniquement les produits IQueens SANS ai_metadata
  const toAnalyze = catalogItems.filter(p =>
    p?.partner === 'iqueens' &&
    p?.id &&
    p?.image &&
    !p?.ai_metadata?.analyzedAt
  ).slice(0, maxPerBatch);

  if (toAnalyze.length === 0) {
    console.log('[AIStylist-Catalog] ✓ Tous les produits IQueens visibles déjà analysés');
    return [];
  }

  console.log(`[AIStylist-Catalog] 🚀 Analyse de ${toAnalyze.length} produits IQueens (categories: ${[...new Set(toAnalyze.map(p => p.category))].join(', ')})`);

  _catalogBatchRunning = true;
  _lastCatalogBatchTime = now;

  const results = [];
  try {
    for (const p of toAnalyze) {
      try {
        const metadata = await analyzeCatalogItem(p, authToken);
        if (metadata) {
          results.push({ id: p.id, metadata });
          // Mettre à jour le cache local catalog_ai_metadata_v1 immédiatement
          try {
            const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
            const raw = await AsyncStorage.getItem('catalog_ai_metadata_v1');
            const cached = raw ? JSON.parse(raw) : {};
            cached[p.id] = metadata;
            await AsyncStorage.setItem('catalog_ai_metadata_v1', JSON.stringify(cached));
          } catch {}
        }
      } catch (e) {
        console.warn(`[AIStylist-Catalog] analyse échec ${p.id}:`, e.message);
      }
      // Petit délai entre appels pour ne pas surcharger l'API
      await new Promise(r => setTimeout(r, 300));
    }
    console.log(`[AIStylist-Catalog] ✓ Batch terminé : ${results.length}/${toAnalyze.length} analysés`);
  } finally {
    _catalogBatchRunning = false;
  }

  return results;
}

// ═══ Code de l'ancien batch côté client gardé en commentaire pour référence ═══
// Si on a besoin de réactiver le fallback côté client (ex: cron Lambda en panne),
// décommenter le bloc ci-dessous et changer le nom de la fonction.
async function _DISABLED_analyzeCatalogInBackground_clientSide(catalogItems, authToken, maxPerBatch = 5, user = null) {
  if (!Array.isArray(catalogItems) || catalogItems.length === 0) return [];
  if (_catalogBatchRunning) return [];
  const now = Date.now();
  if (now - _lastCatalogBatchTime < CATALOG_BATCH_DEBOUNCE_MS) return [];

  // v1.12.305 — garde quota retirée : le batch catalogue background ne doit PLUS
  // compter dans le quota user (seul "ajout au dressing" compte). Le coût Claude
  // est absorbé côté serveur (le user n'a rien demandé d'explicite).

  // ═══ FIX 2026-04 : ne skip QUE si on a déjà un minimum de produits analysés ═══
  // Avant : si batch précédent < 1h → skip inconditionnel. Problème : pour un
  // nouveau user (0 produits analysés), le flag bloquait l'analyse et il ne
  // voyait jamais de trending.
  // Maintenant : on regarde combien de produits iQueens ont ai_metadata.
  // Si < 50 (couverture trop faible), on laisse le batch tourner même si le
  // flag 1h est récent. Dès qu'on passe 50+, on respecte le debounce 1h.
  // ═══ v1.9.11 : étendu à iqueens + tous (les 2 partenaires analysés par IA) ═══
  try {
    // v1.12.337 — étendu à suit_negozi + ann_demeulemeester + coutr (5 partenaires analysés)
    // v1.12.856 — drestige ajouté (6 partenaires analysés)
    const analyzedCount = catalogItems.filter(
      p => (p?.partner === 'iqueens' || p?.partner === 'tous'
            || p?.partner === 'suit_negozi' || p?.partner === 'ann_demeulemeester'
            || p?.partner === 'coutr' || p?.partner === 'drestige') && p?.ai_metadata?.analyzedAt
    ).length;
    const MIN_COVERAGE_TO_SKIP = 50;

    if (analyzedCount >= MIN_COVERAGE_TO_SKIP) {
      const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
      const lastDone = await AsyncStorage.getItem('catalog_last_batch_at');
      if (lastDone && (now - parseInt(lastDone, 10)) < CATALOG_BATCH_COLD_SKIP_MS) {
        console.log(`[AIStylist-Catalog] ⏸️ skip batch : ${analyzedCount} produits déjà analysés, prochain batch dans 1h`);
        return [];
      }
    } else {
      console.log(`[AIStylist-Catalog] 🚀 force batch : seulement ${analyzedCount} produits analysés (< ${MIN_COVERAGE_TO_SKIP}), on continue à remplir`);
    }
  } catch {}

  _catalogBatchRunning = true;
  try {
    // ═══ ANALYSE IA : UNIQUEMENT iQueens POUR L'INSTANT ═══
    // Raison : iQueens a des produits de boutiques partenaires variés et
    // mal catégorisés côté CJ. L'analyse Claude enrichit drastiquement
    // les metadata (couleur, tissu, occasion) pour un meilleur matching.
    // Cashmere : catalogue homogène (tout est en cachemere), analyse IA moins utile.
    // Autres partenaires (Zalando/ASOS/HM) : déjà bien catégorisés.
    //
    // ═══ DÉDUP DÉFENSIVE : éviter d'analyser plusieurs fois le même produit ═══
    // Si l'appelant n'a pas dédupé en amont, deux items iQueens "même article
    // 2 tailles" (avec id différents mais nom-sans-taille identique) seraient
    // tous deux analysés → coût doublé pour résultat identique. On dédupe ici
    // par "nom normalisé + brand + prix" qui regroupe ces variants.
    // ═══ v1.9.11 : ajout TOUS au filtre (avant : seulement iqueens) ═══
    //   Tous les articles de catalogue (iqueens + tous) doivent être
    //   analysés par l'IA pour avoir leur ai_metadata affiché dans la
    //   vue détail.
    const eligibleSeen = new Set();
    const eligible = [];
    for (const p of catalogItems) {
      if (!p || !p.id || !p.image || p.ai_metadata?.analyzedAt) continue;
      // ═══ v1.9.11 : élargi de iqueens à iqueens + tous ═══
      if (p.partner !== 'iqueens' && p.partner !== 'tous') continue;
      const name  = normalizedNameKey(p?.name || '');
      const brand = String(p?.brand || p?.partner || '').trim().toLowerCase();
      const price = Number(p?.price) || 0;
      const key = `np:${brand}|${name}|${price}`;
      if (eligibleSeen.has(key)) continue;
      eligibleSeen.add(key);
      eligible.push(p);
    }

    // ═══ v1.9.11 : Sélection ÉQUITABLE par catégorie (round-robin) ═══
    //   Avant : tri par priorité Hauts/Robes → Sacs/Bijoux/Accessoires
    //     jamais analysés en pratique (toujours en queue de backlog).
    //   Maintenant : on alterne entre toutes les catégories pour garantir
    //     que chaque batch contient un mix équilibré.
    //
    //   Algo : grouper par catégorie, prendre 1 produit de chaque
    //     catégorie tour à tour jusqu'à atteindre maxPerBatch.
    const byCategory = {};
    for (const p of eligible) {
      const cat = p.category || 'Autre';
      if (!byCategory[cat]) byCategory[cat] = [];
      byCategory[cat].push(p);
    }
    // Trier les produits dans chaque catégorie par discount (priorité aux promos)
    for (const cat of Object.keys(byCategory)) {
      byCategory[cat].sort((a, b) => (b.discount || 0) - (a.discount || 0));
    }
    // Round-robin : 1 produit de chaque catégorie tour à tour
    const toAnalyze = [];
    const cats = Object.keys(byCategory);
    let catIdx = 0;
    while (toAnalyze.length < maxPerBatch && cats.some(c => byCategory[c].length > 0)) {
      const cat = cats[catIdx % cats.length];
      if (byCategory[cat].length > 0) {
        toAnalyze.push(byCategory[cat].shift());
      }
      catIdx++;
      // Sécurité : si on fait un tour complet sans rien prendre, on sort
      if (catIdx > cats.length * 100) break;
    }

    // ═══ OPTI 2 : early exit si rien à analyser ═══
    if (toAnalyze.length === 0) {
      console.log('[AIStylist-Catalog] ✅ rien à analyser : tout déjà en DB');
      // Poser le flag pour éviter les retries immédiats sur relance d'app
      try {
        const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
        await AsyncStorage.setItem('catalog_last_batch_at', String(now));
      } catch {}
      return [];
    }

    console.log(`[AIStylist-Catalog] batch: ${toAnalyze.length} products to analyze (cats: ${[...new Set(toAnalyze.map(p => p.category))].join(', ')})`);
    const results = [];
    for (const p of toAnalyze) {
      const metadata = await analyzeCatalogItem(p, authToken);
      if (metadata) results.push({ productId: p.id, metadata });
    }
    console.log(`[AIStylist-Catalog] batch done: ${results.length}/${toAnalyze.length} succès`);

    // Persister le timestamp du dernier batch (succès ou partiel)
    try {
      const AsyncStorage = (await import('@react-native-async-storage/async-storage')).default;
      await AsyncStorage.setItem('catalog_last_batch_at', String(now));
    } catch {}

    return results;
  } finally {
    _catalogBatchRunning = false;
    _lastCatalogBatchTime = Date.now();
  }
}

// ═══════════════════════════════════════════════════════════
// GÉNÉRATION DE TENUES AVANCÉE
// ═══════════════════════════════════════════════════════════

/**
 * Génère les meilleures tenues depuis le dressing
 * Combine algorithme classique + scoring IA si métadonnées disponibles
 */
export function generateSmartOutfits(wardrobeItems, tempC, occasion, maxOutfits = 12) {
  tempC = tempC || 18;
  
  // Séparer par catégorie
  const byCategory = {};
  for (const item of wardrobeItems) {
    if (!byCategory[item.category]) byCategory[item.category] = [];
    byCategory[item.category].push(item);
  }
  
  const outfits = [];
  const hauts = byCategory['Hauts'] || [];
  const bas   = byCategory['Bas'] || [];
  const robes = byCategory['Robes'] || [];
  const vestes = byCategory['Vestes'] || [];
  const chaussures = byCategory['Chaussures'] || [];
  const sacs = byCategory['Sacs'] || [];
  const bijoux = byCategory['Bijoux'] || [];
  const accessoires = byCategory['Accessoires'] || [];
  const accs = [...accessoires, ...bijoux];
  
  // ═══ Robes + accessoires (avec variations bijou/accessoire) ═══
  for (const robe of robes) {
    for (const ch of (chaussures.length ? chaussures : [null])) {
      // Base
      const baseItems = [robe];
      if (ch) baseItems.push(ch);
      if (tempC < 15 && vestes.length) baseItems.push(vestes[0]);
      if (sacs.length) baseItems.push(sacs[0]);

      // Variante 1 : sans accessoire
      if (accs.length === 0) {
        const items = baseItems.slice();
        const score = scoreOutfit(items, tempC, occasion);
        outfits.push({ items, score, occasion: occasion || 'Casual', tempC });
      } else {
        // Variante pour CHAQUE bijou/accessoire : une tenue différente par accessoire
        for (const acc of accs.slice(0, 6)) { // max 6 variantes au lieu de 3
          const items = [...baseItems, acc];
          const score = scoreOutfit(items, tempC, occasion);
          outfits.push({ items, score, occasion: occasion || 'Casual', tempC });
        }
      }
    }
  }
  
  // ═══ Haut + Bas combinaisons (avec variations accessoires) ═══
  for (const haut of hauts) {
    for (const basItem of bas) {
      for (const ch of (chaussures.length ? chaussures : [null])) {
        const baseItems = [haut, basItem];
        if (ch) baseItems.push(ch);
        if (tempC < 15 && vestes.length) baseItems.push(vestes[0]);
        if (sacs.length && occasion !== 'Sport') baseItems.push(sacs[0]);

        // Toujours essayer avec un accessoire (mettre l'accessoire en valeur)
        if (accs.length === 0) {
          const items = baseItems.slice();
          const score = scoreOutfit(items, tempC, occasion);
          outfits.push({ items, score, occasion: occasion || 'Casual', tempC });
        } else {
          // Variante pour chaque accessoire — met vraiment en valeur le bijou
          for (const acc of accs.slice(0, 6)) {
            const items = [...baseItems, acc];
            const score = scoreOutfit(items, tempC, occasion);
            outfits.push({ items, score, occasion: occasion || 'Casual', tempC });
          }
        }
      }
    }
  }

  // ═══ STRATÉGIE "Autour de l'accessoire" ═══
  // Cas : tu viens d'ajouter un bijou / sac / accessoire → on crée une tenue qui le met en vedette
  // même si la base (haut/bas/robe) manque, on l'ajoute comme "suggestion catalogue"
  // Ça permet de proposer une tenue construite autour de l'accessoire
  if (accs.length > 0 && outfits.length < 5) {
    for (const acc of accs) {
      // Tenue minimale autour de l'accessoire (même incomplète, elle sera complétée via catalog)
      const items = [acc];
      if (robes.length > 0) items.unshift(robes[0]);
      else {
        if (hauts.length > 0) items.unshift(hauts[0]);
        if (bas.length > 0) items.unshift(bas[0]);
      }
      if (chaussures.length > 0) items.push(chaussures[0]);

      if (items.length >= 1) {
        const score = scoreOutfit(items, tempC, occasion) * 0.9; // légèrement moins prioritaire
        outfits.push({
          items,
          score,
          occasion: occasion || 'Casual',
          tempC,
          highlightItem: acc, // flag : cette tenue met en valeur cet accessoire
        });
      }
    }
  }
  
  // Trier par score et dédoublonner
  outfits.sort((a, b) => b.score - a.score);
  const seen = new Set();
  const unique = outfits.filter(o => {
    const key = o.items.map(i => i.id).sort().join(',');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  
  // Assigner un id stable à chaque tenue (basé sur les items)
  return unique.slice(0, maxOutfits).map((o, idx) => ({
    ...o,
    id: `outfit_${idx}_${o.items.map(i => i.id || 'x').join('-')}`,
  }));
}

// ═══════════════════════════════════════════════════════════
// HELPER HIGH-LEVEL : suggestions pour un outfit donné
// Wrappe detectMissingPieces + findCatalogSuggestions + replaceTargets
// Retourne une liste PLATE d'articles (pas grouped) pour UI simple
// ═══════════════════════════════════════════════════════════

/**
 * Fonction high-level qui combine :
 *  - détection des pièces manquantes
 *  - recherche de suggestions par catégorie manquante
 *  - si includeReplace : alternatives pour chaque item présent
 *
 * @param {Object} outfit - { items: [...] }
 * @param {Array} catalogItems - catalogue complet
 * @param {Object} options
 *   - tempC: température
 *   - occasion: 'Casual' | 'Chic' | ...
 *   - gender: 'F' | 'M' | 'N'
 *   - maxPicks: nb max d'articles retournés (default 10)
 *   - perCategory: nb par catégorie (default 2)
 *   - alreadySuggestedIds: Set d'ids à exclure
 *   - includeReplace: inclure alternatives (default true)
 * @returns {Array} liste plate d'articles (flat suggestions)
 */
export function getOutfitCatalogSuggestions(outfit, catalogItems, options = {}) {
  const {
    tempC = 18,
    occasion = 'Casual',
    gender = 'F',
    maxPicks = 10,
    perCategory = 2,
    alreadySuggestedIds = new Set(),
    includeReplace = true,
    multiPerCategory = false, // v1.12.272 — si true, retourne plusieurs items par catégorie (jusqu'à perCategory)
  } = options;

  if (!outfit || !Array.isArray(outfit.items) || !Array.isArray(catalogItems)) {
    return [];
  }
  if (catalogItems.length === 0) return [];

  try {
    // 1. Détection des catégories manquantes (ce qui n'est pas dans la tenue)
    const missing = detectMissingPieces(outfit, tempC, occasion) || [];

    // 2. Targets de remplacement (alternatives pour chaque item présent)
    // Filtrer pour ne PAS inclure une catégorie qui est déjà dans `missing`
    // (sinon doublon : missing ['Sacs'] + replace ['Sacs'] si l'user a un sac)
    const missingCategories = new Set(missing.map(m => m.category));
    const replaceTargets = includeReplace
      ? (outfit.items || [])
          .filter(it => it && it.id && !missingCategories.has(it.category))
          .map(item => ({
            category:       item.category,
            reason:         `Alternative à votre ${(item.category || '').toLowerCase()}`,
            order:          99,
            replacesItemId: item.id,
            replacesItem:   item,
            referenceColor: item.color || item.ai_metadata?.primaryColor,
          }))
      : [];

    // 3. Targets = missing en priorité, replaceTargets en complément si besoin
    const allTargets = [...missing, ...replaceTargets];
    if (allTargets.length === 0) return [];

    // 4. Appel findCatalogSuggestions pour obtenir { [category]: [...items] }
    const outfitWithGender = { ...outfit, gender, occasion, tempC };
    const grouped = findCatalogSuggestions(
      outfitWithGender,
      allTargets,
      catalogItems,
      perCategory,
      alreadySuggestedIds
    ) || {};

    // 5. Pools triés par score (tendance d'abord puis _stylistScore)
    const pools = {};
    for (const target of allTargets) {
      const items = grouped[target.category] || [];
      pools[target.category] = [...items].sort((a, b) => {
        const aTrend = a.isTrending ? 1 : 0;
        const bTrend = b.isTrending ? 1 : 0;
        if (aTrend !== bTrend) return bTrend - aTrend;
        return (b._stylistScore || 0) - (a._stylistScore || 0);
      });
    }

    // 6. PASSE 1 : remplir avec les missings
    //  - mode legacy (multiPerCategory=false) : 1 item par catégorie max
    //  - mode multi (multiPerCategory=true) : tous les items du pool jusqu'à perCategory
    const flat = [];
    const seenIds = new Set();
    const seenCategories = new Set();
    for (const target of missing) {
      if (flat.length >= maxPicks) break;
      if (!multiPerCategory && seenCategories.has(target.category)) continue;
      const pool = pools[target.category];
      if (!pool || pool.length === 0) continue;
      const takeN = multiPerCategory ? pool.length : 1;
      for (let i = 0; i < takeN; i++) {
        if (flat.length >= maxPicks) break;
        const p = pool.shift();
        if (!p || !p.id || seenIds.has(p.id)) continue;
        flat.push({
          ...p,
          _replacesLabel: target.reason,
          _isReplacement: false,
        });
        seenIds.add(p.id);
        if (alreadySuggestedIds && typeof alreadySuggestedIds.add === 'function') {
          alreadySuggestedIds.add(p.id);
        }
      }
      seenCategories.add(target.category);
    }

    // 7. PASSE 2 : compléter avec des alternatives si on n'a pas atteint maxPicks
    // ET si includeReplace est true. Sinon on s'arrête là (focus sur ce qui manque).
    if (includeReplace && flat.length < maxPicks) {
      for (const target of replaceTargets) {
        if (flat.length >= maxPicks) break;
        if (!multiPerCategory && seenCategories.has(target.category)) continue;
        const pool = pools[target.category];
        if (!pool || pool.length === 0) continue;
        const takeN = multiPerCategory ? pool.length : 1;
        for (let i = 0; i < takeN; i++) {
          if (flat.length >= maxPicks) break;
          const p = pool.shift();
          if (!p || !p.id || seenIds.has(p.id)) continue;
          flat.push({
            ...p,
            _replacesItemId: target.replacesItemId,
            _replacesItem:   target.replacesItem,
            _replacesLabel:  target.reason,
            _isReplacement:  true,
          });
          seenIds.add(p.id);
          if (alreadySuggestedIds && typeof alreadySuggestedIds.add === 'function') {
            alreadySuggestedIds.add(p.id);
          }
        }
        seenCategories.add(target.category);
      }
    }

    return flat;
  } catch (e) {
    console.warn('[getOutfitCatalogSuggestions] error:', e.message);
    return [];
  }
}
